import { compose } from 'recompose';
import { withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router';

import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import MuiAppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Button from 'components/CncUI/Button';

import TabMenu from './TabMenu';
import UserMenu from './UserMenu';
import NotificationsMenu from './NotificationsMenu';
import CountryTabs from './CountryTabs';

import cncLogo from 'images/MyCast&CrewLogo.svg';
import TabMenuData from './utils/menuOptions';
import filterTabs from './utils/filterTabs';

import useCountry from 'hooks/useCountry';
import useAuthentication from 'hooks/useAuthentication';
import useFeatureFlags from 'hooks/useFeatureFlags';
import { useMediaQuery } from 'react-responsive';

const styles = ({ palette, breakpoints, typography }) => ({
  root: {
    zIndex: 10,
    boxShadow: palette.cncUI.shadow.container.default,
  },
  mainToolbar: {
    backgroundColor: palette.cncUI.default.primary,
    minHeight: 60,
    [breakpoints.up('md')]: {
      padding: 0,
    },
    [breakpoints.down('md')]: {
      maxWidth: '100vw',
      padding: 0,
    },
    [breakpoints.only('xs')]: {
      '& .userMenu': {
        padding: 0,
      },
      '& .notificationMenu': {
        padding: 0,
        maxWidth: 40,
        marginLeft: -20,
        marginRight: 8,
      },
      '& .mainToolbar': {
        margin: 0,
      },
    },
    '& .mainToolbar': {
      margin: '-8px 8px',
    },
    '& .mainToolbarLeft': {
      flexGrow: 1,
      [breakpoints.only('xs')]: {
        padding: '8px 0',
      },
    },
    '& .logo': {
      marginRight: 18,
    },
  },
  logoContainer: {
    boxSizing: 'border-box',
    alignSelf: 'center',
    height: '100%',
    width: 'auto',
    ...typography.appName,
    gridArea: 'title',
    color: palette.cncUI.primary.gray['+0'],
    margin: '0 12px',
    maxHeight: 60,
    [breakpoints.up('md')]: {
      margin: '0 64px 0 12px',
    },
  },
  logo: {
    [breakpoints.only('xs')]: {
      width: 100,
    },
  },
  tab: {
    background: palette.cncUI.default.primary,
    color: palette.cncUI.default['+10'],
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: '16px',
    '& img, & span': {
      margin: '0 8px',
    },
    '& img': {
      marginRight: 0,
    },
  },
  active: {
    background: palette.cncUI.default['+10'],
    color: palette.cncUI.default.primary,
  },
  appToolbar: {
    maxWidth: '100vw',
    minHeight: 50,
    backgroundColor: palette.cncUI.default['+10'],
  },
  adminToolbar: {
    maxWidth: '100vw',
    minHeight: '36px',
    borderTop: `0.5px solid ${palette.cncUI.primary.gray['+9']}`,
    backgroundColor: palette.cncUI.default['+10'],
  },
  cncButton: {
    color: palette.cncUI.default['+10'],
    '&:hover, &:focus, &:active': {
      color: palette.cncUI.default['+10'],
    },
    '&:hover': {
      backgroundColor: palette.cncUI.default['+3'],
    },
    [breakpoints.down('md')]: {
      display: 'none',
    },
  },
});

const HOME_PAGE_LINK = 'https://www.castandcrew.com';
const FLAG_NOTIFICATIONS = 'MyCnC-DisplayNotifications';

const AppBar = props => {
  const { classes, location: { pathname } = {} } = props;
  const { permissions } = useAuthentication();
  const { country } = useCountry();
  const { isEnabled } = useFeatureFlags();
  const isMobile = useMediaQuery({
    query: '(max-width: 960px)',
  });

  const TabMenuCountry =
    country === 'CAN'
      ? [...TabMenuData.main, ...TabMenuData.canada, ...TabMenuData.mainAdmin]
      : [...TabMenuData.main, ...TabMenuData.usa, ...TabMenuData.mainAdmin];

  const mainTabs = filterTabs(TabMenuCountry, isEnabled, permissions);
  const adminTabs = filterTabs(TabMenuData.admin, isEnabled, permissions);
  const offerTabs = filterTabs(
    TabMenuData.offer[country],
    isEnabled,
    permissions,
  );

  const isOfferPage = pathname.startsWith('/offer');

  return (
    <MuiAppBar classes={{ root: classes.root }} position="sticky">
      <Toolbar className={classes.mainToolbar}>
        <Hidden xsDown>
          <Grid
            container
            spacing={2}
            alignItems="center"
            className="mainToolbar"
          >
            <Grid item className="mainToolbarLeft">
              <Grid container spacing={2} style={{ minHeight: 60 }}>
                <Grid item className={classes.logoContainer}>
                  <Hidden key="old-logo" smDown>
                    <img
                      src={cncLogo}
                      alt="My Cast and Crew Logo"
                      data-test-id="AppBar-logo"
                      className={classes.logo}
                    />
                  </Hidden>
                  <Hidden key="new-logo" mdUp>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/MyCast&CrewLogo.svg`}
                      alt="My Cast and Crew Logo"
                      data-test-id="AppBar-logo"
                      className={classes.logo}
                    />
                  </Hidden>
                </Grid>
                <CountryTabs isMobile={isMobile} />
              </Grid>
            </Grid>
            <Grid item>
              <Grid container spacing={0}>
                <Grid item style={{ display: 'flex' }}>
                  <Button
                    tertiary
                    className={classes.cncButton}
                    href={HOME_PAGE_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Cast & Crew Homepage
                  </Button>
                </Grid>
                {isEnabled(FLAG_NOTIFICATIONS) && (
                  <Grid item className="notificationMenu">
                    <NotificationsMenu />
                  </Grid>
                )}
                <Grid item className="userMenu" style={{ display: 'flex' }}>
                  <UserMenu />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Hidden>
        <Hidden smUp>
          <Grid
            container
            spacing={2}
            alignItems="center"
            className="mainToolbar"
          >
            <Grid item className="mainToolbarLeft" xs={9}>
              <Grid container spacing={2} style={{ minHeight: 60 }}>
                <Grid item className={classes.logoContainer}>
                  <Hidden key="old-logo" smDown>
                    <img
                      src={cncLogo}
                      alt="My Cast and Crew Logo"
                      data-test-id="AppBar-logo"
                      className={classes.logo}
                    />
                  </Hidden>
                  <Hidden key="new-logo" mdUp>
                    <img
                      src={`${process.env.PUBLIC_URL}/images/MyCast&CrewLogo.svg`}
                      alt="My Cast and Crew Logo"
                      data-test-id="AppBar-logo"
                      className={classes.logo}
                    />
                  </Hidden>
                </Grid>
                <CountryTabs isMobile={isMobile} />
              </Grid>
            </Grid>
            <Grid item xs={3}>
              <Grid container spacing={0} alignContent="flex-end">
                <Hidden key="home-link" smDown>
                  <Grid item>
                    <Button
                      tertiary
                      className={classes.cncButton}
                      href={HOME_PAGE_LINK}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cast & Crew Homepage
                    </Button>
                  </Grid>
                </Hidden>
                {isEnabled(FLAG_NOTIFICATIONS) && (
                  <Grid item xs={6} className="notificationMenu">
                    <NotificationsMenu />
                  </Grid>
                )}
                <Grid
                  item
                  xs={6}
                  className="userMenu"
                  style={{ display: 'flex' }}
                >
                  <UserMenu />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Hidden>
      </Toolbar>
      <Toolbar className={classes.appToolbar}>
        <TabMenu menuData={isOfferPage ? offerTabs : mainTabs} />
      </Toolbar>
      {pathname.startsWith('/admin') && (
        <Toolbar className={classes.adminToolbar}>
          <TabMenu menuData={adminTabs} />
        </Toolbar>
      )}
    </MuiAppBar>
  );
};

export default compose(withRouter, withStyles(styles))(AppBar);
